/**
 * Brings the part of a mask that refused a save into view, and puts the cursor
 * on the control that fixes it.
 *
 * A mask says what is missing in a snackbar, which is read at the top of the
 * screen while the field waits further down — on the invoice even on another
 * step. "Bei Validierungsfehlern zum ersten fehlerhaften Feld springen und den
 * Grund nennen" (GF 12.10.2026, #2206 R-134).
 *
 * The anchors are data attributes rather than ids: a mask may show the same
 * block twice (a document and its copy side by side in a dialog), and an id
 * would then be ambiguous.
 */
export const paloReveal = (anchor: string, focus?: string): void => {
    // After the snackbar, so the scroll is not undone by the layout settling.
    window.setTimeout(() => {
        const target = document.querySelector<HTMLElement>(`[data-palo-anchor="${anchor}"]`);

        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        if (!focus) {
            return;
        }

        const control = document
            .querySelector<HTMLElement>(`[data-palo-anchor="${focus}"]`)
            ?.querySelector<HTMLElement>('button, input, [tabindex]');

        control?.focus({ preventScroll: true });
    }, 0);
};

/**
 * Brings the first field the mask marked as faulty into view and puts the
 * cursor in it.
 *
 * "Bei Validierungsfehlern zum ersten fehlerhaften Feld springen" (GF
 * 12.10.2026). The head of a document and its positions are two forms, and
 * neither was submitted when the save was refused from the document's own
 * button, so nothing moved the view — the red marks sat above the fold
 * (#2206, R-135). Reads the marks off the page rather than the form state,
 * which keeps it right for whichever mask uses it.
 */
export const paloRevealFirstError = (): void => {
    window.setTimeout(() => {
        // The input first: a marked field also marks its label, and the label
        // comes earlier in the document but holds nothing to focus.
        const faulty =
            document.querySelector<HTMLElement>('[aria-invalid="true"]') ??
            document.querySelector<HTMLElement>('.Mui-error');

        if (!faulty) {
            return;
        }

        const control = faulty.matches('input, textarea')
            ? faulty
            : faulty.querySelector<HTMLElement>('input, textarea') ??
              faulty.closest('.MuiFormControl-root')?.querySelector<HTMLElement>('input, textarea') ??
              null;

        (control ?? faulty).scrollIntoView({ behavior: 'smooth', block: 'center' });
        control?.focus({ preventScroll: true });
    }, 0);
};
